import { useState, useEffect } from "react";
import "./LoyaltyPanel.scss";
// Import a chart library for the donut chart - using Chart.js
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
// Add icons import - assuming you're using react-icons
import {
  FiCopy,
  FiGift,
  FiTag,
  FiChevronDown,
  FiChevronUp,
  FiPackage,
  FiDollarSign,
  FiShoppingCart,
  FiHeart,
  FiStar,
} from "react-icons/fi";
import {
  MdOutlineDiscount,
  MdOutlineDiamond,
  MdOutlineCurrencyRupee,
} from "react-icons/md";
import { useAuthContext } from "../../firebase-files/hooks/useAuthContext";
import { useNavigate } from "react-router-dom";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const LoyaltyPanel = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  // Sample data for the donut chart

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  });

  const chartData = {
    labels: ["Topwear", "Bottomwear", "Shoes", "Occasion Wear", "Accessories"],
    datasets: [
      {
        data: [30, 20, 25, 15, 10],
        backgroundColor: [
          "rgba(255, 107, 107, 0.9)", // Brighter red
          "rgba(78, 205, 196, 0.9)", // Turquoise
          "rgba(69, 183, 209, 0.9)", // Blue
          "rgba(150, 206, 180, 0.9)", // Mint
          "rgba(255, 238, 173, 0.9)", // Yellow
        ],
        borderColor: [
          "rgba(255, 107, 107, 1)",
          "rgba(78, 205, 196, 1)",
          "rgba(69, 183, 209, 1)",
          "rgba(150, 206, 180, 1)",
          "rgba(255, 238, 173, 1)",
        ],
        borderWidth: 2,
        borderRadius: 10,
        spacing: 5,
        hoverOffset: 15,
      },
    ],
  };

  const chartOptions = {
    plugins: {
      legend: {
        position: "right",
        labels: {
          padding: 20,
          font: {
            size: 14,
            family: "'Inter', sans-serif",
          },
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#2C3E50",
        bodyColor: "#2C3E50",
        bodyFont: {
          size: 14,
          family: "'Inter', sans-serif",
        },
        padding: 12,
        boxPadding: 8,
        borderColor: "#e0e4e7",
        borderWidth: 1,
        displayColors: true,
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.raw || 0;
            return `${label}: ${value}%`;
          },
        },
      },
    },
    cutout: "75%",
    radius: "90%",
    animation: {
      animateScale: true,
      animateRotate: true,
    },
  };

  // Sample user data
  const userData = {
    name: user?.fullName,
    image: "https://cdn-icons-png.flaticon.com/512/149/149071.png",
    mobile: `+${user?.phoneNumber.slice(0, 2)} ${user?.phoneNumber.slice(2)}`,
    points: 10500,
    availablePoints: 6800,
    tier: "Diamond Member",
    joinedDate: `${user?.createdAt.toDate().toLocaleDateString(`en-US`, {
      year: "numeric",
      month: "long",
      day: "numeric",
    })}`,
    membershipColor: "#7E57C2", // Color for diamond tier
  };

  const [activeTab, setActiveTab] = useState("coupons");

  // Sample offers data
  const offers = [
    {
      id: 1,
      image:
        "https://images.unsplash.com/photo-1483985988355-763728e1935b?ixlib=rb-4.0.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      title: "Summer Collection",
      description:
        "Get up to 50% off on summer collection. Limited time offer!",
      validTill: "2024-04-30",
    },
    {
      id: 2,
      image:
        "https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      title: "Exclusive Membership",
      description:
        "Join our exclusive membership program and get additional benefits",
      validTill: "2024-05-15",
    },
    {
      id: 3,
      image:
        "https://images.unsplash.com/photo-1490114538077-0a7f8cb49891?ixlib=rb-4.0.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      title: "New Arrivals",
      description:
        "Check out our latest collection. Fresh styles just dropped!",
      validTill: "2024-05-01",
    },
  ];

  // Sample coupons data
  const availableCoupons = [
    {
      id: 1,
      code: "SAVE20",
      discount: "20% OFF",
      description:
        "Get 20% off on all regular-priced items. Valid for all categories.",
      validTill: "April 30, 2024",
    },
    {
      id: 2,
      code: "FREESHIP",
      discount: "Free Shipping",
      description:
        "Enjoy free shipping on orders above ₹999. Pan India delivery.",
      validTill: "April 25, 2024",
    },
    {
      id: 3,
      code: "FIRST50",
      discount: "₹500 OFF",
      description: "Flat ₹500 off on your first purchase above ₹2499.",
      validTill: "May 15, 2024",
    },
  ];

  // Sample deals data
  const availableDeals = [
    {
      id: 1,
      title: "Premium Brand Voucher",
      value: "₹2000",
      pointsRequired: 2000,
      description: "Get a ₹2000 voucher for premium brands",
    },
    {
      id: 2,
      title: "VIP Shopping Pass",
      value: "1 Month",
      pointsRequired: 1500,
      description: "Early access to sales & exclusive events",
    },
    {
      id: 3,
      title: "Luxury Beauty Box",
      value: "Worth ₹5000",
      pointsRequired: 3000,
      description: "Curated luxury beauty products",
    },
  ];

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    // You might want to add a toast notification here
  };

  // Sample transactions
  const recentTransactions = [
    {
      id: 1,
      date: "2024-03-15",
      item: "Blue Denim Jacket",
      points: "+150",
      amount: "₹2999",
    },
    {
      id: 2,
      date: "2024-03-10",
      item: "Running Shoes",
      points: "+200",
      amount: "₹3999",
    },
    {
      id: 3,
      date: "2024-03-05",
      item: "Casual Shirt",
      points: "+100",
      amount: "₹1999",
    },
    {
      id: 4,
      date: "2024-02-28",
      item: "Party Wear",
      points: "+180",
      amount: "₹3599",
    },
    {
      id: 5,
      date: "2024-02-25",
      item: "Accessories Set",
      points: "+75",
      amount: "₹1499",
    },
  ];

  // Add state for modal
  // const [showTierModal, setShowTierModal] = useState(false);

  // // Add tier data
  // const tierData = {
  //     tiers: [
  //         { name: 'Gold', pointsNeeded: 1000, color: '#FFD700' },
  //         { name: 'Platinum', pointsNeeded: 2000, color: '#E5E4E2' },
  //         { name: 'Diamond', pointsNeeded: 3000, color: '#B9F2FF' },
  //         { name: 'Ruby', pointsNeeded: 5000, color: '#E0115F' }
  //     ],
  //     currentPoints: 2500, // User's current points
  //     currentTier: 'Diamond' // User's current tier
  // };

  // Add this sample data after other sample data (before the return statement)
  const pointsTransactions = [
    {
      id: 1,
      rewardName: "Premium Brand Voucher",
      pointsUsed: 2000,
      redeemedOn: "2024-03-20",
      status: "Active",
      validTill: "2024-04-20",
    },
    {
      id: 2,
      rewardName: "VIP Shopping Pass",
      pointsUsed: 1500,
      redeemedOn: "2024-03-15",
      status: "Active",
      validTill: "2024-04-15",
    },
    {
      id: 3,
      rewardName: "Luxury Beauty Box",
      pointsUsed: 3000,
      redeemedOn: "2024-03-01",
      status: "Expired",
      validTill: "2024-03-31",
    },
    {
      id: 4,
      rewardName: "Free Shipping Voucher",
      pointsUsed: 500,
      redeemedOn: "2024-02-28",
      status: "Used",
      validTill: "2024-03-28",
    },
  ];

  // Add state for section visibility
  const [expandedSections, setExpandedSections] = useState({
    offers: true,
    pointsHistory: true,
    rewards: true,
    transactions: true,
  });

  // Add toggle function
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Modify the loyalty card content to include the info button
  const userProfileSection = (
    <div className="user-profile">
      <div className="profile-image">
        <img src={userData.image} alt={userData.name} />
        <div className="tier-badge">
          <div className="tier-icon">
            <MdOutlineDiamond size={20} />
          </div>
          {userData.tier.split(" ")[0]}
        </div>
      </div>
      <div className="user-info">
        <div className="name-and-info">
          <h2>{userData.name}</h2>
          {/* <button 
                        className="tier-info-btn"
                        onClick={() => setShowTierModal(true)}
                    >
                        <FiInfo />
                        <span>Know More</span>
                    </button> */}
        </div>
        <p className="mobile">{userData.mobile}</p>
        <p className="joined">Member since {userData.joinedDate}</p>
      </div>
    </div>
  );

  // Update the TierModal component
  // const TierModal = () => {
  //     // Find the next tier
  //     const currentTierIndex = tierData.tiers.findIndex(t => t.name === tierData.currentTier);
  //     const nextTier = tierData.tiers[currentTierIndex + 1];

  //     // Calculate progress percentage between current tier and next tier
  //     const calculateProgress = () => {
  //         if (!nextTier) return 100; // If at max tier

  //         const currentTierPoints = tierData.tiers[currentTierIndex].pointsNeeded;
  //         const pointsForNextTier = nextTier.pointsNeeded - currentTierPoints;
  //         const userProgressInTier = tierData.currentPoints - currentTierPoints;

  //         return Math.min(100, (userProgressInTier / pointsForNextTier) * 100);
  //     };

  //     return (
  //         <div className="tier-modal-overlay" onClick={() => setShowTierModal(false)}>
  //             <div className="tier-modal" onClick={e => e.stopPropagation()}>
  //                 <button className="close-btn" onClick={() => setShowTierModal(false)}>
  //                     <FiX />
  //                 </button>
  //                 <h2>Your Loyalty Journey</h2>

  //                 <div className="tier-progress-container">
  //                     <div className="tier-track">
  //                         {tierData.tiers.map((tier, index) => (
  //                             <div
  //                                 key={tier.name}
  //                                 className={`tier-node ${tierData.currentPoints >= tier.pointsNeeded ? 'completed' : ''
  //                                     } ${tier.name === tierData.currentTier ? 'current' : ''}`}
  //                             >
  //                                 <span className="tier-label">{tier.name}</span>
  //                                 <div className="tier-icon" style={{ backgroundColor: tier.color }}>
  //                                     {tierData.currentPoints >= tier.pointsNeeded && (
  //                                         <span className="check-icon">✓</span>
  //                                     )}
  //                                 </div>
  //                                 <div className="points-needed">{tier.pointsNeeded} Points</div>
  //                                 {index < tierData.tiers.length - 1 && (
  //                                     <div className={`connector ${tierData.currentPoints >= tier.pointsNeeded ? 'completed' : ''
  //                                         }`} />
  //                                 )}
  //                             </div>
  //                         ))}
  //                     </div>

  //                     <div className="current-progress">
  //                         <div className="progress-text">
  //                             <span>Current Tier Progress</span>
  //                             <span className="points">
  //                                 {tierData.currentPoints} / {nextTier ? nextTier.pointsNeeded : tierData.currentPoints} Points
  //                             </span>
  //                         </div>
  //                         <div className="progress-bar">
  //                             <div
  //                                 className="progress-fill"
  //                                 style={{
  //                                     width: `${calculateProgress()}%`
  //                                 }}
  //                             />
  //                         </div>
  //                     </div>
  //                 </div>
  //             </div>
  //         </div>
  //     );
  // };

  return (
    <div className="loyalty-panel-container">
      {/* <div className="loyalty-header">
                <h1>My Points</h1>
            </div> */}

      <div className="top-section">
        <div className="loyalty-card">
          <div className="card-pattern"></div>
          <div className="card-circles">
            <div className="circle circle-1"></div>
            <div className="circle circle-2"></div>
            <div className="circle circle-3"></div>
          </div>
          <div className="card-content">
            {userProfileSection}
            <div className="points-info">
              <div className="total-points">
                <span className="points">{userData.points}</span>
                <span className="points-label">Total Points</span>
              </div>
              <div className="available-points">
                <span className="points">{userData.availablePoints}</span>
                <span className="points-label">Available Points</span>
              </div>
            </div>
          </div>
        </div>

        <div className="category-distribution">
          <h2>Shopping Categories</h2>
          <div className="chart-container">
            <Doughnut data={chartData} options={chartOptions} />
          </div>
        </div>
      </div>

      {/* Add Quick Menu Section */}
      <div className="bb-quick-menu-lp">
        <div
          className="menu-item"
          onClick={() => {
            navigate("/orders");
          }}
        >
          <div className="icon-circle">
            <FiPackage /> {/* For Orders */}
          </div>
          <span>Orders</span>
        </div>
        <div className="menu-item">
          <div className="icon-circle">
            <MdOutlineCurrencyRupee /> {/* For Transactions */}
          </div>
          <span>Transactions</span>
        </div>
        <div
          className="menu-item"
          onClick={() => {
            navigate("/cart");
          }}
        >
          <div className="icon-circle">
            <FiShoppingCart /> {/* For Cart */}
          </div>
          <span>Cart</span>
        </div>
        <div
          className="menu-item"
          onClick={() => {
            navigate("/wishlist");
          }}
        >
          <div className="icon-circle">
            <FiHeart /> {/* For Wishlist */}
          </div>
          <span>Wishlist</span>
        </div>
        <div className="menu-item">
          <div className="icon-circle">
            <MdOutlineDiscount /> {/* For Returns */}
          </div>
          <span>Deals & Offers</span>
        </div>
        <div className="menu-item">
          <div className="icon-circle">
            <FiStar /> {/* For Reviews */}
          </div>
          <span>Reviews & Ratings</span>
        </div>
      </div>

      <div className="bb-offers-section">
        <div className="section-header" onClick={() => toggleSection("offers")}>
          <h2>Special Offers</h2>
          <span className="bb-toggle-icon">
            {expandedSections.offers ? <FiChevronUp /> : <FiChevronDown />}
          </span>
        </div>
        {expandedSections.offers && (
          <div className="bb-offers-slider">
            {offers.map((offer) => (
              <div key={offer.id} className="bb-offer-card">
                <div className="bb-offer-content">
                  <h3>{offer.title}</h3>
                  <p>{offer.description}</p>
                  <span className="valid-till">
                    Valid till: {offer.validTill}
                  </span>
                </div>
                <div className="bb-offer-image">
                  <img src={offer.image} alt={offer.title} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="points-history-section">
        <div
          className="section-header"
          onClick={() => toggleSection("pointsHistory")}
        >
          <h2>Points Redemption History</h2>
          <span className="bb-toggle-icon">
            {expandedSections.pointsHistory ? (
              <FiChevronUp />
            ) : (
              <FiChevronDown />
            )}
          </span>
        </div>
        {expandedSections.pointsHistory && (
          <div className="history-container">
            {pointsTransactions.map((transaction) => (
              <div key={transaction.id} className="history-card">
                <div className="reward-info">
                  <h3>{transaction.rewardName}</h3>
                  <span
                    className={`status ${transaction.status.toLowerCase()}`}
                  >
                    {transaction.status}
                  </span>
                </div>
                <div className="transaction-details">
                  <div className="detail">
                    <span className="label">Points Used:</span>
                    <span className="value points">
                      {transaction.pointsUsed}
                    </span>
                  </div>
                  <div className="detail">
                    <span className="label">Redeemed On:</span>
                    <span className="value">{transaction.redeemedOn}</span>
                  </div>
                  <div className="detail">
                    <span className="label">Valid Till:</span>
                    <span className="value">{transaction.validTill}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rewards-section">
        <div
          className="section-header"
          onClick={() => toggleSection("rewards")}
        >
          <h2>Available Rewards</h2>
          <span className="bb-toggle-icon">
            {expandedSections.rewards ? <FiChevronUp /> : <FiChevronDown />}
          </span>
        </div>
        {expandedSections.rewards && (
          <>
            <div className="tabs">
              <button
                className={`tab ${activeTab === "coupons" ? "active" : ""}`}
                onClick={() => setActiveTab("coupons")}
              >
                <FiTag /> Coupons
              </button>
              <button
                className={`tab ${activeTab === "deals" ? "active" : ""}`}
                onClick={() => setActiveTab("deals")}
              >
                <FiGift /> Deals
              </button>
            </div>
            <div className="rewards-container">
              {activeTab === "coupons" ? (
                <div className="coupons-grid">
                  {availableCoupons.map((coupon) => (
                    <div key={coupon.id} className="coupon-card">
                      <div className="coupon-content">
                        <h3>{coupon.discount}</h3>
                        <p className="description">{coupon.description}</p>
                        <p className="validity">
                          Valid till: {coupon.validTill}
                        </p>
                        <div className="code-section">
                          <code>{coupon.code}</code>
                          <button
                            className="copy-btn"
                            onClick={() => handleCopyCode(coupon.code)}
                          >
                            <FiCopy /> Copy
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="deals-grid">
                  {availableDeals.map((deal) => (
                    <div key={deal.id} className="deal-card">
                      <div className="deal-content">
                        <h3>{deal.title}</h3>
                        <p className="value">Value: {deal.value}</p>
                        <p className="description">{deal.description}</p>
                        <div className="points-section">
                          <span className="points-required">
                            {deal.pointsRequired} Points
                          </span>
                          <button className="redeem-btn">Redeem</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="transactions-section">
        <div
          className="section-header"
          onClick={() => toggleSection("transactions")}
        >
          <h2>Recent Transactions</h2>
          <span className="bb-toggle-icon">
            {expandedSections.transactions ? (
              <FiChevronUp />
            ) : (
              <FiChevronDown />
            )}
          </span>
        </div>
        {expandedSections.transactions && (
          <div className="transactions-container">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Item</th>
                  <th>Points Earned</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{transaction.date}</td>
                    <td>{transaction.item}</td>
                    <td className="points">{transaction.points}</td>
                    <td>{transaction.amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* {showTierModal && <TierModal />} */}
    </div>
  );
};

export default LoyaltyPanel;
